import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import Cookies from 'js-cookie';
import type {
  BreakdownItem,
  RouteQueryParams as HotelParityInsightParams,
} from 'lumen/routes/market/parity/insight';
import type {
  RouteQueryParams,
  TrendsDataRequestParams,
  TrendsDataResponse,
} from 'lumen/routes/market/parity/insight/trends';
import type { Transition } from 'lumen/services/transition';
import { MEMBERSHIPTYPES } from 'lumen/utils/constants/general-constants';
import type { Breakdown } from 'lumen/utils/constants/pi-constant-values';
import {
  HISTORICAL_POINTS_AMOUNT,
  PI_BREAKDOWNS,
} from 'lumen/utils/constants/pi-constant-values';
import generateFakeName from 'lumen/utils/parity-analytics/pa-generate-fake-name';
import { formatUrl } from 'lumen/utils/rates-request';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

const CONFIDENTIAL_BREAKDOWNS: Breakdown[] = [
  PI_BREAKDOWNS.MARKET_ID,
  PI_BREAKDOWNS.BRAND_ID,
  PI_BREAKDOWNS.SUBSCRIPTION_ID,
] as const;

export default class HotelParityInsightTrendsRoute extends Route {
  queryParams = {
    segmentation: {
      refreshModel: true,
    },
    selection: {
      refreshModel: false,
    },
    trendsExtractDate: {
      refreshModel: true,
    },
  };

  @service declare ajax: Services['ajax'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare transition: Services['transition'];

  isDemoModeActive = Cookies.get('demoModev3') !== undefined;

  fetchTrendsDataTask = task(async (params: RouteQueryParams) => {
    try {
      const {
        dateAggregation,
        extractDate,
        segmentationFilters,
        los,
        mealPlan,
        pos,
        rateType,
        showMemberRate,
      } = this.paramsFor('hotel.parity.insight') as HotelParityInsightParams;

      const requestParams: TrendsDataRequestParams = {
        'aggregations[]': params.segmentation,
        extract_date: params.trendsExtractDate ?? extractDate,
        date_aggregation: dateAggregation,
        periods: HISTORICAL_POINTS_AMOUNT,
        los: los,
        mealtype: mealPlan,
        pos: pos,
        rate_type: rateType,
        subscription_id: this.hotelAndMarketService.hotelOrMarket.id,
      };

      if (segmentationFilters.length) {
        requestParams.filters = segmentationFilters;
      }

      if (showMemberRate) {
        requestParams.base_membership_type = MEMBERSHIPTYPES.MEMBER_ONLY;
      }

      const trendsData: TrendsDataResponse = await this.ajax.request(
        formatUrl(
          '/apigateway/v1/app/parityanalytics/api/single_property',
          requestParams,
          this.ajax.host,
        ),
        {
          method: 'GET',
          dataType: 'json',
          contentType: 'application/json; charset=utf-8',
        },
      );

      if (this.isDemoModeActive) {
        this.demoTransform(trendsData);
      }

      return trendsData[params.segmentation];
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  beforeModel(transition: Transition): void {
    this.transition.validateParityInsightTrendsQueryParams(transition);
  }

  demoTransform(trendsData: TrendsDataResponse): void {
    CONFIDENTIAL_BREAKDOWNS.forEach((breakdown) => {
      if (trendsData[breakdown]) {
        Object.keys(trendsData[breakdown]).forEach((extractDate) => {
          (trendsData[breakdown][extractDate] as BreakdownItem[]).forEach(
            (value) => {
              value.segmentation_value = generateFakeName(value, breakdown);
            },
          );
        });
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: RouteQueryParams) {
    return {
      trendsData: this.fetchTrendsDataTask.perform(params),
    };
  }
}
